import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiArrowRight, FiUsers, FiDollarSign, FiHome, FiHeart, FiShield, FiCalendar } = FiIcons;

const QuestionnaireFlow = ({ responses, updateResponses }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepResponses, setStepResponses] = useState(responses);
  const navigate = useNavigate();

  const questions = [
    {
      id: 'age',
      title: "What's your age?",
      subtitle: "Your age affects both your insurance needs and the rates you'll pay",
      icon: FiCalendar,
      type: 'single',
      options: [
        { value: '20-30', label: '20-30 years old', description: 'Building your career and possibly starting a family' },
        { value: '31-40', label: '31-40 years old', description: 'Peak family and career building years' },
        { value: '41-50', label: '41-50 years old', description: 'Highest earning potential with major responsibilities' },
        { value: '51-65', label: '51-65 years old', description: 'Approaching retirement but still need protection' },
        { value: '66+', label: '66+ years old', description: 'Focus on final expenses and legacy planning' }
      ]
    },
    {
      id: 'familyStatus',
      title: "Who depends on your income?",
      subtitle: "The more people who rely on you financially, the more coverage you need",
      icon: FiUsers,
      type: 'single',
      options: [
        { value: 'single', label: 'Just me', description: 'Minimal life insurance needs, mainly final expenses' },
        { value: 'married', label: 'My spouse/partner', description: 'Need to protect your partner\'s financial future' },
        { value: 'young-kids', label: 'Spouse + young children', description: 'Maximum protection needed for growing family' },
        { value: 'adult-kids', label: 'Spouse + adult children', description: 'Moderate protection, kids are more independent' },
        { value: 'single-parent', label: 'I\'m a single parent', description: 'Critical coverage - you\'re the sole provider' }
      ]
    },
    {
      id: 'income',
      title: "What's your household income?",
      subtitle: "Your income determines how much your family would need to replace if you're gone",
      icon: FiDollarSign,
      type: 'single',
      options: [
        { value: 'under-50k', label: 'Under $50,000', description: 'Focus on essential coverage and final expenses' },
        { value: '50k-100k', label: '$50,000 - $100,000', description: 'Standard coverage to maintain lifestyle' },
        { value: '100k-200k', label: '$100,000 - $200,000', description: 'Higher coverage to protect comfortable lifestyle' },
        { value: '200k-500k', label: '$200,000 - $500,000', description: 'Substantial coverage for high-income protection' },
        { value: 'over-500k', label: 'Over $500,000', description: 'Maximum coverage for wealth preservation' }
      ]
    },
    {
      id: 'debts',
      title: "What major debts would your family inherit?",
      subtitle: "Life insurance should cover debts so your family isn't burdened with payments",
      icon: FiHome,
      type: 'multiple',
      options: [
        { value: 'mortgage', label: 'Mortgage', description: 'Home loan that needs to be paid off' },
        { value: 'student-loans', label: 'Student loans', description: 'Educational debt that may transfer to family' },
        { value: 'credit-cards', label: 'Credit card debt', description: 'Outstanding balances on credit accounts' },
        { value: 'business-loans', label: 'Business loans', description: 'Commercial debt tied to your business' },
        { value: 'auto-loans', label: 'Car loans', description: 'Vehicle financing that needs coverage' },
        { value: 'none', label: 'No major debts', description: 'Debt-free means less coverage needed' }
      ]
    },
    {
      id: 'goals',
      title: "What do you want life insurance to accomplish?",
      subtitle: "Understanding your goals helps determine the right type and amount of coverage",
      icon: FiShield,
      type: 'multiple',
      options: [
        { value: 'income-replacement', label: 'Replace my income', description: 'Help family maintain their current lifestyle' },
        { value: 'debt-coverage', label: 'Pay off all debts', description: 'Clear mortgage, loans, and credit cards' },
        { value: 'education-funding', label: 'Pay for kids\' college', description: 'Ensure children can still get education' },
        { value: 'final-expenses', label: 'Cover funeral costs', description: 'Don\'t burden family with end-of-life expenses' },
        { value: 'wealth-transfer', label: 'Leave money to heirs', description: 'Create an inheritance for your family' },
        { value: 'business-protection', label: 'Protect my business', description: 'Keep business running or help partners buy out your share' }
      ]
    },
    {
      id: 'timeline',
      title: "How long do you need this protection?",
      subtitle: "This determines whether term life (temporary) or permanent life insurance is better",
      icon: FiCalendar,
      type: 'single',
      options: [
        { value: '10-years', label: '10 years or less', description: 'Short-term protection, cheapest option' },
        { value: '20-years', label: '15-20 years', description: 'Most popular choice, covers key family years' },
        { value: '30-years', label: '25-30 years', description: 'Long-term protection through retirement' },
        { value: 'lifetime', label: 'My entire lifetime', description: 'Permanent coverage that never expires' },
        { value: 'unsure', label: 'I\'m not sure', description: 'We\'ll help you figure this out' }
      ]
    },
    {
      id: 'health',
      title: "How's your health?",
      subtitle: "Your health affects your rates and which insurance companies will accept you",
      icon: FiHeart,
      type: 'single',
      options: [
        { value: 'excellent', label: 'Excellent health', description: 'No health issues, very active, best rates' },
        { value: 'good', label: 'Good health', description: 'Generally healthy with minor manageable issues' },
        { value: 'fair', label: 'Fair health', description: 'Some health conditions but stable' },
        { value: 'poor', label: 'Poor health', description: 'Significant health challenges, may need special coverage' },
        { value: 'prefer-not-say', label: 'I\'d rather not say', description: 'We\'ll discuss this during application' }
      ]
    }
  ];

  const handleOptionSelect = (questionId, optionValue) => {
    const question = questions[currentStep];
    let newValue;

    if (question.type === 'multiple') {
      const currentValues = stepResponses[questionId] || [];
      if (optionValue === 'none') {
        newValue = ['none'];
      } else {
        const filteredValues = currentValues.filter(v => v !== 'none');
        if (filteredValues.includes(optionValue)) {
          newValue = filteredValues.filter(v => v !== optionValue);
        } else {
          newValue = [...filteredValues, optionValue];
        }
      }
    } else {
      newValue = optionValue;
    }

    setStepResponses(prev => ({
      ...prev,
      [questionId]: newValue
    }));
  };

  const isOptionSelected = (questionId, optionValue) => {
    const question = questions[currentStep];
    const currentValue = stepResponses[questionId];

    if (question.type === 'multiple') {
      return currentValue && currentValue.includes(optionValue);
    }
    return currentValue === optionValue;
  };

  const canProceed = () => {
    const currentQuestion = questions[currentStep];
    const currentValue = stepResponses[currentQuestion.id];

    if (currentQuestion.type === 'multiple') {
      return currentValue && currentValue.length > 0;
    }
    return currentValue !== undefined;
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Save responses and go to results page
      updateResponses(stepResponses);
      navigate('/results');
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/');
    }
  };

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm font-medium text-gray-600">
              Question {currentStep + 1} of {questions.length}
            </span>
            <span className="text-sm font-medium text-gray-600">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
          >
            {/* Question Header */}
            <div className="text-center mb-12">
              <div className="w-16 h-16 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={currentQuestion.icon} className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {currentQuestion.title}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {currentQuestion.subtitle}
              </p>
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
              {currentQuestion.options.map((option, index) => (
                <motion.button
                  key={option.value}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => handleOptionSelect(currentQuestion.id, option.value)}
                  className={`p-6 rounded-xl border-2 transition-all duration-300 text-left hover:shadow-lg ${
                    isOptionSelected(currentQuestion.id, option.value)
                      ? 'border-primary-500 bg-primary-50 shadow-md'
                      : 'border-gray-200 hover:border-primary-300'
                  }`}
                >
                  <div className="flex items-start">
                    <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 mt-1 mr-4 ${
                      isOptionSelected(currentQuestion.id, option.value)
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-gray-300'
                    }`}>
                      {isOptionSelected(currentQuestion.id, option.value) && (
                        <div className="w-full h-full rounded-full bg-white scale-50"></div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {option.label}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {option.description}
                      </p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <button
                onClick={handlePrevious}
                className="flex items-center px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors duration-200"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2" />
                {currentStep === 0 ? 'Back to Home' : 'Previous'}
              </button>

              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className={`flex items-center px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  canProceed()
                    ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {currentStep === questions.length - 1 ? 'See My Recommendations' : 'Continue'}
                <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default QuestionnaireFlow;